import axios from 'axios'

function stripTrailingSlash(url) {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

let authRedirectInProgress = false

const AUTH_REDIRECT_AT_KEY = '__lb_auth_redirect_at'
const AUTH_REDIRECT_COOLDOWN_MS = 5000

function getAuthRedirectAt() {
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_AT_KEY)
    const ts = raw ? Number(raw) : null
    return Number.isFinite(ts) ? ts : null
  } catch (e) {
    return null
  }
}

function setAuthRedirectAt(ts) {
  try {
    sessionStorage.setItem(AUTH_REDIRECT_AT_KEY, String(ts))
  } catch (e) {
    // ignore
  }
}

function clearWebStoragePreservingAuthMarker() {
  const marker = getAuthRedirectAt()
  try {
    localStorage.clear()
  } catch (e) {
    // ignore
  }
  try {
    sessionStorage.clear()
  } catch (e) {
    // ignore
  }
  if (marker) setAuthRedirectAt(marker)
}

/**
 * Clears non-auth cookies to prevent stale order/session state from being sent to the backend.
 * '__ac' cookie carries the user's login credentials and must be preserved so the user
 * stays logged in after a stale purge.
 * 'NovaDine.session' and other session cookies are cleared so the backend doesn't
 * associate the new order with the old expired session.
 */
function clearNonAuthCookies() {
  const PRESERVE = ['__ac']
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0]
      if (!name || PRESERVE.includes(name)) return
      // Delete for current path and root path to be safe
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
    })
  } catch (e) {
    console.warn('[clearNonAuthCookies] Failed to clear cookies:', e)
  }
}

function hardRedirectHome() {
  window.location.assign('/')
}

const useRelativeProxy = String(process.env.VUE_APP_USE_RELATIVE_PROXY).toLowerCase() === 'true'
const tenantBase = process.env.VUE_APP_TENANT
const baseURL = !useRelativeProxy && tenantBase ? stripTrailingSlash(tenantBase) : '/api'
const ndApiKeyWeb = process.env.VUE_APP_ND_API_KEY_WEB
const ndApiKeyMobile = process.env.VUE_APP_ND_API_KEY_MOBILE

function isMobileWeb() {
  return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(navigator.userAgent)
}

const httpClient = axios.create({
  baseURL,
  withCredentials: true,
})

// 20 minutes in milliseconds
const STALE_TIMEOUT = 20 * 60 * 1000

// Request interceptor: optional auth header + stale check
httpClient.interceptors.request.use(
  async (config) => {
    const ndApiKey = isMobileWeb() ? ndApiKeyMobile : ndApiKeyWeb
    if (ndApiKey) {
      if (!config.headers) config.headers = {}
      if (!config.headers['ND-API-Key']) config.headers['ND-API-Key'] = ndApiKey
    }

    const authRaw = localStorage.getItem('auth')
    if (authRaw) {
      const auth = String(authRaw).trim()
      const isValidBasic = /^Basic\s+[A-Za-z0-9+/=]+$/.test(auth) && !/\b(undefined|null)\b/i.test(auth)
      if (isValidBasic) {
        config.headers.Authorization = auth
      } else if (/^Basic\b/i.test(auth)) {
        localStorage.removeItem('auth')
      }
    }

    const rawPath = window?.location?.pathname || ''
    const path = rawPath.replace(/\/+$/, '')
    const href = window?.location?.href || ''
    const isOrderQuoteRoute = /(^|\/)order-quote$/.test(path) || /(^|\/)order-quote(\/|\?|#)/.test(href)

    const lastRequestRaw = localStorage.getItem('last_api_request')
    const lastRequest = lastRequestRaw ? Number(lastRequestRaw) : null
    const now = Date.now()

    if (Number.isFinite(lastRequest) && now - lastRequest > STALE_TIMEOUT) {
      console.log(
        `[API Request] Stale data detected. Last request was ${Math.round((now - lastRequest) / 1000)}s ago. Purging${
          isOrderQuoteRoute ? '...' : ' and redirecting...'
        }`
      )

      // Update the timestamp so this and subsequent requests are not treated as stale
      localStorage.setItem('last_api_request', String(now))

      const { default: store } = await import('@/store')

      // Purge Vuex state (order, cart, location — but NOT user/auth)
      store.commit('PURGE_STALE_DATA')

      // Clear non-auth cookies so the backend doesn't receive the old session/order context.
      // '__ac' is preserved so the user remains logged in.
      clearNonAuthCookies()

      if (!isOrderQuoteRoute) {
        const { default: router } = await import('@/router')
        router.push('/')
      }

      return config
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor: normalize response shape + handle auth errors
httpClient.interceptors.response.use(
  (response) => {
    // Update last request time on successful response
    localStorage.setItem('last_api_request', Date.now())

    // Ensure `response.data.data` exists for all successful responses.
    const body = response?.data
    const isObjectLike = body !== null && typeof body === 'object'
    const alreadyWrapped = isObjectLike && Object.prototype.hasOwnProperty.call(body, 'data')
    if (!alreadyWrapped) response.data = { data: body }

    return response
  },
  async (error) => {
    // Normalize error response structure for backwards compatibility
    if (error?.response?.data) {
      const body = error.response.data
      const isObjectLike = body !== null && typeof body === 'object'
      const alreadyWrapped = isObjectLike && Object.prototype.hasOwnProperty.call(body, 'data')

      if (!alreadyWrapped && isObjectLike && body.error) {
        error.response.data = { data: body }
      }
    }

    // On auth errors (401/403), reset storage and return user to home.
    const status = error?.response?.status
    const rawPath = window?.location?.pathname || ''
    const path = rawPath.replace(/\/+$/, '')
    const href = window?.location?.href || ''
    const isDeepLinkFlow =
      /(^|\/)order-quote$/.test(path) ||
      /(^|\/)checkin$/.test(path) ||
      /(^|\/)order-quote(\/|\?|#)/.test(href) ||
      /(^|\/)checkin(\/|\?|#)/.test(href)
    const now = Date.now()
    const lastRedirectAt = getAuthRedirectAt()
    const inCooldown = Number.isFinite(lastRedirectAt) && now - lastRedirectAt < AUTH_REDIRECT_COOLDOWN_MS

    if ((status === 401 || status === 403) && !authRedirectInProgress && !inCooldown) {
      authRedirectInProgress = true
      setAuthRedirectAt(now)
      console.warn(`[API Response] Auth error (${status}) detected. Clearing web storage and redirecting to home...`)
      if (!isDeepLinkFlow) {
        clearWebStoragePreservingAuthMarker()
        hardRedirectHome()
      }
    }
    return Promise.reject(error)
  }
)

export default httpClient